// Embed.jsx - Free charts, tickers and calculators for anybody's website, and
// the builder that writes the line of HTML.
//
// Everything on /tools is embeddable, not just the price charts. A jeweller's
// blog wanting a scrap gold calculator in its sidebar is the same trade as a
// news site wanting a chart: they get a genuinely useful tool, we get one small
// link on a bar at the bottom.
//
// The argument this page makes is the same one the rest of the site makes: we
// publish the inputs. A live metal price is a public fact, so giving the chart
// away costs us nothing and puts our alerts in front of people who are already
// interested. Every widget carries one thin bar with "Stay updated" on it, and
// that is the entire ask. There is no key, no account, no attribution
// requirement, and no expiry.
//
// Two audiences, deliberately served differently:
//
//   A person gets the builder. Click, watch the preview change, copy one line.
//   A model or a developer gets section 5, which is the URL contract written
//   out in plain text so it can be read once and used without this page.
//
// The widget url is a file, /widget.html, not a directory. See the note in
// vite.config.js: a trailing slash somebody forgets is a not found page.
//
// The gallery previews are drawn in React without the bar, because somebody
// choosing between four widgets wants to see the tool, not our name four times.
// The builder preview is the real iframe, because what somebody copies has to be
// what they saw.
//
// LEGEND
//   1. PRESETS      the starting points, in two groups
//   2. buildUrl()   config in, widget url out
//   3. Builder      the controls, the live preview, and the snippet
//   4. Gallery      pick a starting point
//   5. Contract     the parameters, in plain text, for anybody automating this
//   6. Embed        the page

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import clsx from "clsx";

import { Seo, JsonLd, breadcrumbSchema } from "../lib/seo.jsx";
import { useAsync } from "../lib/hooks.js";
import { publicApi } from "../lib/api.js";
import { siteOrigin } from "../lib/host.js";
import { METALS, RANGES, metalOf } from "../lib/chart.js";
import { PriceChart, Sparkline, Change } from "../ui/chart.jsx";
import { Panel, Checkbox, CopyButton, Alert } from "../ui/kit.jsx";
import { Check, ArrowRight, Grid } from "../ui/icons.jsx";
import { Reveal } from "../ui/motion.jsx";
import { PageHead, CtaBand } from "./parts.jsx";
import { AlertForm } from "./StayUpdated.jsx";

const ORIGIN = () => siteOrigin() || "https://internationalgoldandsilver.com";

////////// 1. PRESETS //////////
// Grouped, because a flat list of seven is a form and two lists of three or four
// is two choices. Each preset is a whole configuration, so picking one and
// copying is a two click path from arriving.
//
// `kind` decides which controls the builder shows and which preview the gallery
// draws. Tools have no range and no axis; charts have no unit.

const PRESETS = [
  {
    key: "chart",
    title: "Price chart",
    body: "One metal, a real line, and a range switcher your readers can use. The one most people want.",
    config: { type: "chart", metals: ["gold"], range: "1m", theme: "light", axis: true, title: true, switcher: true, height: 340 },
  },
  {
    key: "ticker",
    title: "Four metal ticker",
    body: "Gold, silver, platinum and palladium in a strip, each with the shape of its own week beside it.",
    config: { type: "ticker", metals: ["gold", "silver", "platinum", "palladium"], range: "1w", theme: "light", axis: false, title: false, switcher: false, height: 190 },
  },
  {
    key: "compact",
    title: "Single price line",
    body: "One metal, one line, forty four pixels tall. For a header, a sidebar, or the top of a post.",
    config: { type: "ticker", metals: ["gold"], range: "1d", theme: "light", axis: false, title: false, switcher: false, height: 76 },
  },
  {
    key: "dark",
    title: "Chart, no furniture",
    body: "The line and nothing else, on a transparent background, so it takes the colours of the page it lands on.",
    config: { type: "chart", metals: ["gold"], range: "1y", theme: "transparent", axis: false, title: true, switcher: false, height: 260 },
  },
  {
    key: "calculator",
    group: "Calculators",
    title: "Scrap gold calculator",
    body: "Weight in, pure gold content and its value at spot out. The most useful thing on our site, and it works the same on yours.",
    config: { type: "calculator", metals: ["gold"], range: "1m", theme: "light", axis: false, title: true, switcher: false, unit: "g", height: 250 },
  },
  {
    key: "karat",
    group: "Calculators",
    title: "Hallmark chart",
    body: "What 585, 925, PLAT, GF and EPNS actually mean, with the metal content of each. One metal at a time, switchable.",
    config: { type: "karat", metals: ["gold"], range: "1m", theme: "light", axis: false, title: true, switcher: true, unit: "g", height: 360 },
  },
  {
    key: "converter",
    group: "Calculators",
    title: "Weight converter",
    body: "Grams, pennyweight, troy ounces, standard ounces and pounds at once. The troy ounce mix-up is worth ten percent, so this earns its place.",
    config: { type: "converter", metals: ["gold"], range: "1m", theme: "light", axis: false, title: true, switcher: false, unit: "g", height: 260 },
  },
];

// Everything that is not a price line. These share the calculator controls and
// none of the chart ones.
const TOOL_TYPES = ["calculator", "karat", "converter"];
const isTool = (type) => TOOL_TYPES.includes(type);

const KINDS = [
  { value: "chart", label: "Chart" },
  { value: "ticker", label: "Ticker" },
  { value: "calculator", label: "Calculator" },
  { value: "karat", label: "Hallmarks" },
  { value: "converter", label: "Converter" },
];

const KIND_HEIGHT = { chart: 340, ticker: 190, calculator: 250, karat: 360, converter: 260 };

const UNIT_OPTIONS = [
  { value: "g", label: "Grams" },
  { value: "dwt", label: "dwt" },
  { value: "ozt", label: "ozt" },
];

// The hallmark chart only has tables for the three we publish marks for.
const KARAT_METALS = ["gold", "silver", "platinum"];

////////// 2. URL //////////
// The only place a widget url is assembled. `grant` is the pair the builder
// mints when somebody turns our name off; it is deliberately not something a
// person can type, so it is never written into the documentation in section 5.
//
// Absolute for the snippet, because it is going onto somebody else's website.
// Relative for the preview on this page, because it is not. That difference
// matters more than it looks: this page is prerendered, so an absolute url would
// be baked into the static html at build time and the preview on a local or a
// staging copy would quietly load the production widget. It also keeps the
// server rendered markup identical to the first client render.

function widgetQuery(config, grant) {
  const query = new URLSearchParams();
  if (config.type !== "chart") query.set("type", config.type);
  query.set(config.metals.length > 1 ? "metals" : "metal", config.metals.join(","));

  if (isTool(config.type)) {
    // A tool has no range and no axis. What it has instead is the unit its
    // readers weigh in, which is the one thing worth setting for an audience.
    if (config.unit && config.unit !== "g") query.set("unit", config.unit);
  } else {
    query.set("range", config.range);
    if (!config.axis) query.set("axis", "0");
  }

  if (config.theme !== "light") query.set("theme", config.theme);
  if (!config.title) query.set("title", "0");
  if (!config.switcher) query.set("switcher", "0");
  if (grant) { query.set("e", grant.id); query.set("k", grant.key); }
  return `/widget.html?${query}`;
}

const buildUrl = (config, grant) => `${ORIGIN()}${widgetQuery(config, grant)}`;

const TITLES = {
  ticker: () => "Live precious metal prices",
  calculator: (metal) => `${metal.label} calculator`,
  karat: () => "Hallmark chart: what the mark means",
  converter: () => "Precious metal weight converter",
  chart: (metal) => `Live ${metal.label.toLowerCase()} price chart`,
};

const snippetFor = (config, grant) => {
  const label = (TITLES[config.type] || TITLES.chart)(metalOf(config.metals[0]));

  return `<iframe src="${buildUrl(config, grant)}"\n  title="${label}" width="100%" height="${config.height}" loading="lazy"\n  style="border:0;max-width:100%;display:block"></iframe>`;
};

////////// 3. BUILDER //////////

function Builder({ config, setConfig }) {
  const [grant, setGrant] = useState(null);
  const [minting, setMinting] = useState(false);
  const [problem, setProblem] = useState(null);

  const branded = !grant;
  const set = (patch) => setConfig((previous) => ({ ...previous, ...patch }));

  // Turning our name off is the one setting that is not a query parameter, so it
  // costs a request. Turning it back on is free: the key is simply dropped.
  const setBranded = useCallback(async (next) => {
    setProblem(null);
    if (next) { setGrant(null); return; }
    setMinting(true);
    try {
      setGrant(await publicApi.createEmbed({ note: "builder" }));
    } catch {
      setProblem("We could not switch that off just now. Try again in a moment, or leave it on and copy it anyway.");
    } finally {
      setMinting(false);
    }
  }, []);

  // What somebody copies is absolute. What this page loads is not.
  const snippet = snippetFor(config, grant);
  const previewUrl = widgetQuery(config, grant);

  const toggleMetal = (key) => {
    if (config.type !== "ticker") return set({ metals: [key] });
    const has = config.metals.includes(key);
    if (has && config.metals.length === 1) return undefined;
    return set({ metals: has ? config.metals.filter((metal) => metal !== key) : [...config.metals, key].slice(0, 4) });
  };

  return (
    <div className="grid gap-8 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:gap-10 items-start">
      {/* ---- controls ---- */}
      <Panel className="p-5 sm:p-6">
        <p className="eyebrow">Build it</p>
        <h2 className="display display-md text-ink mt-2">Set it up, copy one line.</h2>

        <Row label="Kind">
          <Segment
            value={config.type}
            onChange={(value) => set({
              type: value,
              metals: value === "ticker" ? config.metals : config.metals.slice(0, 1),
              height: value === "ticker" ? Math.max(68, config.metals.length * 46 + 24) : KIND_HEIGHT[value],
            })}
            options={KINDS}
          />
        </Row>

        <Row
          label={config.type === "ticker" ? "Metals" : "Metal"}
          hint={config.type === "ticker" ? "Pick up to four." : config.type === "converter" ? "Only used for the spot price line at the bottom." : undefined}
        >
          <Segment
            value={config.metals}
            multiple={config.type === "ticker"}
            onChange={toggleMetal}
            options={METALS
              .filter((metal) => config.type !== "karat" || KARAT_METALS.includes(metal.key))
              .map((metal) => ({ value: metal.key, label: metal.label }))}
          />
        </Row>

        {isTool(config.type) && config.type !== "karat" && (
          <Row label="Weighed in" hint="What the box starts on. Your readers can change it.">
            <Segment value={config.unit || "g"} onChange={(value) => set({ unit: value })} options={UNIT_OPTIONS} />
          </Row>
        )}

        {!isTool(config.type) && (
          <Row label="Range">
            <Segment
              value={config.range}
              onChange={(value) => set({ range: value })}
              options={RANGES.map((range) => ({ value: range.key, label: range.short }))}
            />
          </Row>
        )}

        <Row label="Theme" hint="Transparent takes the background of the page it sits on.">
          <Segment
            value={config.theme}
            onChange={(value) => set({ theme: value })}
            options={[
              { value: "light", label: "Light" },
              { value: "dark", label: "Dark" },
              { value: "auto", label: "Auto" },
              { value: "transparent", label: "None" },
            ]}
          />
        </Row>

        <Row label="Height" hint="Width is always 100% of whatever you put it in.">
          <div className="flex items-center gap-3">
            <input
              type="range"
              min="56"
              max="600"
              step="2"
              value={config.height}
              onChange={(event) => set({ height: Number(event.target.value) })}
              className="flex-1 accent-bronze"
              aria-label="Height in pixels"
            />
            <span className="num text-[13.5px] text-muted w-14 text-right">{config.height}px</span>
          </div>
        </Row>

        <div className="mt-5 pt-5 border-t border-rule-soft space-y-3">
          {config.type === "chart" && (
            <>
              <Checkbox checked={config.title} onChange={(event) => set({ title: event.target.checked })} label="Show the name and the price above the chart" />
              <Checkbox checked={config.axis} onChange={(event) => set({ axis: event.target.checked })} label="Show the axis labels" />
              <Checkbox checked={config.switcher} onChange={(event) => set({ switcher: event.target.checked })} label="Let readers change the time range" />
            </>
          )}

          {isTool(config.type) && (
            <>
              <Checkbox checked={config.title} onChange={(event) => set({ title: event.target.checked })} label="Show the heading" />
              {config.type === "karat" && (
                <Checkbox checked={config.switcher} onChange={(event) => set({ switcher: event.target.checked })} label="Let readers switch between gold, silver and platinum" />
              )}
            </>
          )}

          <Checkbox
            checked={branded}
            onChange={(event) => setBranded(event.target.checked)}
            label={minting ? "Switching that off" : "Show our name in the bottom bar"}
            hint="Off is fine. The bar keeps its Stay updated link either way, and that is the only thing we ask for."
          />
        </div>

        {problem && <Alert tone="warning" className="mt-4">{problem}</Alert>}

        <p className="fineprint mt-5 text-[12.5px] text-faint leading-relaxed">
          Free, with no key and no account. Leave it up for as long as you like. We will not start charging
          for one that is already on a page, and there is nothing here that tracks your readers.
        </p>
      </Panel>

      {/* ---- preview and snippet ---- */}
      <div>
        <div className="flex items-baseline justify-between gap-4">
          <p className="eyebrow">Exactly what you will get</p>
          <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="text-[13px] text-muted hover:text-ink underline decoration-rule hover:decoration-bronze">
            Open on its own
          </a>
        </div>

        {/* A real iframe of the real widget. A drawn imitation would be a nicer
            preview and a worse promise. */}
        <div className="mt-3 p-4 sm:p-6 bg-surface border border-rule">
          <iframe
            key={previewUrl}
            src={previewUrl}
            title="Widget preview"
            width="100%"
            height={config.height}
            loading="lazy"
            style={{ border: 0, maxWidth: "100%", display: "block" }}
          />
        </div>

        <div className="mt-5 flex items-center justify-between gap-4">
          <p className="eyebrow">Paste this</p>
          <CopyButton value={snippet} label="Copy the code" className="btn-quiet btn-sm" />
        </div>
        <pre className="snippet mt-2">{snippet}</pre>

        <ul className="mt-4 grid gap-2 text-[13.5px] text-muted">
          {[
            "Works in WordPress, Squarespace, Wix, Webflow, Ghost, Notion and plain HTML.",
            "Never scrolls and never gets wider than the box you put it in.",
            "Updates itself every minute. Nothing to maintain.",
            "A calculator answers in your reader's browser, so it keeps working on a slow connection.",
          ].map((line) => (
            <li key={line} className="flex gap-2.5">
              <Check size={16} className="text-positive shrink-0 mt-0.5" />
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Row({ label, hint, children }) {
  return (
    <div className="mt-5">
      <p className="label mb-2">{label}</p>
      {children}
      {hint && <p className="hint mt-1.5">{hint}</p>}
    </div>
  );
}

function Segment({ value, onChange, options, multiple = false }) {
  const isOn = (option) => (multiple ? value.includes(option.value) : (Array.isArray(value) ? value[0] : value) === option.value);

  return (
    <div className="seg" role="group">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className="seg-item"
          aria-pressed={isOn(option)}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

////////// 4. GALLERY //////////
// Drawn in React, with no bottom bar. Somebody deciding between seven widgets is
// looking at the tool, and seven copies of our own name would be the only thing
// on the screen.
//
// Two groups rather than one list. Somebody arrives wanting either a price or a
// calculator, and has already made that choice before they read a word.

// A still of each tool, drawn rather than framed. An iframe per card would be
// seven requests to decide one thing.
function ToolPreview({ type }) {
  const rows = {
    calculator: [["14K, 10 grams", "5.83 g"], ["Value at spot", "$450.12"]],
    karat: [["18K, 750", "75.0%"], ["14K, 585", "58.3%"], ["GF, RGP, HGE", "none"]],
    converter: [["Pennyweight", "6.430"], ["Troy ounces", "0.3215"], ["Standard ounces", "0.353"]],
  }[type] || [];

  return (
    <span className="block space-y-1.5">
      {rows.map(([label, value]) => (
        <span key={label} className="flex items-center justify-between gap-3 border-t border-rule-soft pt-1.5 first:border-0 first:pt-0">
          <span className="text-[13px] text-muted">{label}</span>
          <span className="text-[13px] font-semibold text-ink num">{value}</span>
        </span>
      ))}
    </span>
  );
}

function Gallery({ onPick, current }) {
  const { data } = useAsync((signal) => publicApi.chart({ metals: "gold,silver,platinum,palladium", range: "1w", sparkline: true }, signal), []);
  const byMetal = Object.fromEntries((data?.series || []).map((line) => [line.metal, line]));
  const groups = ["Prices", "Calculators"];

  return (
    <div className="space-y-8">
      {groups.map((group) => (
        <div key={group}>
          <p className="eyebrow mb-3">{group}</p>
          <GalleryGrid
            presets={PRESETS.filter((preset) => (preset.group || "Prices") === group)}
            onPick={onPick}
            current={current}
            byMetal={byMetal}
          />
        </div>
      ))}
    </div>
  );
}

function GalleryGrid({ presets, onPick, current, byMetal }) {
  return (
    <div className="grid gap-px bg-rule border border-rule sm:grid-cols-2">
      {presets.map((preset) => {
        const active = preset.key === current;
        return (
          <button
            key={preset.key}
            type="button"
            onClick={() => onPick(preset)}
            className={clsx("text-left p-5 sm:p-6 transition-colors", active ? "bg-bronze-wash" : "bg-paper hover:bg-surface")}
          >
            <span className="flex items-start justify-between gap-4">
              <span className="display display-sm text-ink">{preset.title}</span>
              {active
                ? <span className="tag tag-bronze shrink-0">Editing</span>
                : <ArrowRight size={17} className="text-faint shrink-0 mt-1.5" />}
            </span>
            <span className="block hint mt-2 leading-relaxed">{preset.body}</span>

            <span className="block mt-4 pt-4 border-t border-rule-soft">
              {isTool(preset.config.type) ? (
                <ToolPreview type={preset.config.type} />
              ) : preset.config.type === "chart" ? (
                <PriceChart
                  points={byMetal[preset.config.metals[0]]?.points || []}
                  range="1w"
                  color={metalOf(preset.config.metals[0]).color}
                  height={72}
                  axis={false}
                  live={false}
                  className="h-[72px]"
                  label={`${preset.title} example`}
                />
              ) : (
                <span className="block space-y-1.5">
                  {preset.config.metals.map((key) => (
                    <span key={key} className="flex items-center justify-between gap-3">
                      <span className="text-[13px] text-muted">{metalOf(key).label}</span>
                      <span className="flex items-center gap-3">
                        <Sparkline points={byMetal[key]?.points || []} color={metalOf(key).color} width={56} height={16} />
                        <Change percent={byMetal[key]?.changePercent} className="text-[12px] w-14 text-right" />
                      </span>
                    </span>
                  ))}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

////////// 5. THE CONTRACT //////////
// Written out rather than demonstrated, because the other reader of this page is
// a language model putting a chart on somebody's site, and it needs the rule
// rather than an example. Everything here is stable. If a parameter ever has to
// change, the old spelling keeps working.

const CONTRACT = `International Gold & Silver: free precious metal embeds.
Price charts, tickers, scrap metal calculators, a hallmark chart and a weight
converter. One URL, one parameter picks which.

Base URL
  https://internationalgoldandsilver.com/widget.html

Use it in an iframe. No API key, no account, no registration, no attribution
requirement, no rate limit for normal page use. Free to use permanently,
including on commercial sites.

  <iframe src="https://internationalgoldandsilver.com/widget.html?metal=gold&range=1m"
    title="Live gold price chart" width="100%" height="340" loading="lazy"
    style="border:0;max-width:100%;display:block"></iframe>

  <iframe src="https://internationalgoldandsilver.com/widget.html?type=calculator&metal=gold"
    title="Scrap gold calculator" width="100%" height="250" loading="lazy"
    style="border:0;max-width:100%;display:block"></iframe>

Parameters
  type       chart | ticker | calculator | karat | converter   default chart
  metal      gold | silver | platinum | palladium  default gold
             karat has tables for gold, silver and platinum only
  metals     comma separated, up to 4, ticker only
  theme      light | dark | auto | transparent     default light
  accent     a 6 digit hex colour, e.g. 8a6534     default: per metal
  title      0 to hide the heading                 default 1

  Charts and tickers only
  range      1d | 1w | 1m | 6m | 1y | 5y           default 1m
  axis       0 to hide the axis labels             default 1
  switcher   0 to hide the range buttons           default 1

  Calculators only
  unit       g | dwt | ozt | lb                    default g
  weight     the number the box starts on          default 10
  purity     a purity key, e.g. 14k, 925, 950      default: the common one
  switcher   karat only: 0 hides the metal buttons default 1

Behaviour
  Width is always 100% of the iframe. Set the height on the iframe. 340 suits a
  chart, 44 to 60 per row suits a ticker, 250 a calculator, 360 the hallmark
  chart and 260 the converter.
  It never scrolls and never overflows its box.
  Prices refresh once a minute. A calculator answers as somebody types, in their
  browser, and a price refresh never interrupts them mid-entry.
  theme=transparent paints no background, so it takes the host page's own.
  A 30px bar at the bottom carries one button, "Stay updated", pointing at our
  free price alerts. That is what the widget costs.

  A calculator shows metal content at spot and never an offer. That is the same
  rule our own pages follow: we publish the inputs, so anybody can check the
  arithmetic, and what any buyer pays is a separate conversation.

Data
  US dollars per troy ounce, spot. Readings inside a week are our own, taken
  every ten minutes. Longer ranges are built from daily settlement data scaled
  to spot. Not investment advice.

JSON, if you would rather draw it yourself
  GET https://api.internationalgoldandsilver.com/api/public/chart?metals=gold&range=1m
  Public, CORS open, cached for 60 seconds.
  Returns { range, series: [ { metal, price, perGram, changePercent, points } ] }
  where points is [[unixMilliseconds, usdPerTroyOunce], ...]

  GET https://api.internationalgoldandsilver.com/api/public/spot?tools=1
  The prices and the purity tables the calculators run on, in one response.
  Returns { perGram, perOunce, updatedAt, purities } where purities is
  { gold: [ { value, label, factor } ], silver: [...], ... } and factor is the
  proportion of the alloy that is the metal.`;

function Contract() {
  return (
    <section className="mt-14 md:mt-20">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Reveal as="h2" className="display display-lg text-ink">For developers, and for whatever is writing your HTML</Reveal>
          <p className="lede mt-4 measure-wide text-muted">
            The whole contract on one screen. Hand this to an assistant and it has everything it needs
            without opening this page.
          </p>
        </div>
        <CopyButton value={CONTRACT} label="Copy all of it" className="btn-quiet btn-sm" />
      </div>

      <pre className="snippet mt-6 text-[11.5px] sm:text-[12px]">{CONTRACT}</pre>
    </section>
  );
}

////////// 6. THE PAGE //////////

export default function Embed() {
  const [params] = useSearchParams();
  const [preset, setPreset] = useState("chart");
  const [config, setConfig] = useState(PRESETS[0].config);

  // Arriving from a tool or a chart elsewhere on the site should open the
  // builder on that thing, not on the default one. Every tool page links here
  // with its own type and metal.
  useEffect(() => {
    const tool = params.get("tool");
    const metal = params.get("metal");
    const range = params.get("range");
    if (!tool && !metal && !range) return;

    const starting = PRESETS.find((option) => option.config.type === tool);
    if (starting) setPreset(starting.key);

    setConfig((previous) => ({
      ...(starting ? starting.config : previous),
      ...(METALS.some((option) => option.key === metal) ? { metals: [metal] } : {}),
      ...(RANGES.some((option) => option.key === range) ? { range } : {}),
    }));
  }, [params]);

  const pick = useCallback((chosen) => {
    setPreset(chosen.key);
    setConfig(chosen.config);
  }, []);

  const schema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Precious metal charts and calculators to embed",
    applicationCategory: "WebApplication",
    operatingSystem: "Any",
    description: "Free gold, silver, platinum and palladium price charts, tickers, scrap metal calculators, a hallmark chart and a weight converter to embed on any website. No account and no API key.",
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  }), []);

  return (
    <>
      <Seo
        title="Free gold and silver charts and calculators for your website"
        path="/embed"
        description="Free, unbranded precious metal price charts, tickers, scrap gold and silver calculators, a hallmark chart and a weight converter. One line of HTML, no account, no API key, no attribution required."
      />
      <JsonLd data={breadcrumbSchema([{ label: "Home", to: "/" }, { label: "Embeds", to: "/embed" }])} />
      <JsonLd data={schema} />

      <PageHead
        eyebrow="Free, no account, no key"
        title="Put our tools on your site."
        lede="One line of HTML gives you a live metal chart, a scrap gold calculator, a hallmark chart or a weight converter. They update themselves, they work on a phone, and you can turn our name off. Keep them up for as long as you like."
      >
        <div className="flex flex-wrap gap-3">
          <a href="#builder" className="btn btn-primary">Build one <Grid size={16} /></a>
          <Link to="/prices" className="btn btn-quiet">See the live charts first</Link>
        </div>
      </PageHead>

      <div className="shell pb-14 md:pb-20">
        <Gallery onPick={pick} current={preset} />

        <div id="builder" className="mt-10 md:mt-14 scroll-mt-24">
          <Builder config={config} setConfig={setConfig} />
        </div>

        <Contract />

        {/* The other half of the trade, said plainly, on the page where somebody
            has just taken something for nothing. */}
        <section className="mt-14 md:mt-20 grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.85fr)] lg:gap-12 items-start">
          <div>
            <Reveal as="h2" className="display display-lg text-ink">Why this is free</Reveal>
            <p className="lede mt-4 measure-wide text-muted">
              We buy gold and silver by post. A live price is a public fact, so publishing it costs us
              nothing and puts us in front of people who are already thinking about metal.
            </p>
            <p className="hint mt-4 leading-relaxed measure-wide">
              The only thing a widget asks of your readers is one small link to our free price alerts. No
              tracking pixel, no cookie, no script on your page beyond the iframe itself, and nothing that
              follows anybody around. If that ever changes, it will change on new embeds and not on yours.
            </p>
          </div>
          <AlertForm source="embed" />
        </section>
      </div>

      <CtaBand
        title="Selling rather than watching?"
        body="Send us the actual pieces in a free insured kit. We weigh, test and quote every item on camera, and you see the whole breakdown before you decide anything."
      />
    </>
  );
}
